// ==============================================
// Plan changes in words: what the review-before-save step and the plan's
// history show, e.g. "Monthly price: NZ$26.00 → NZ$29.00".
// ==============================================

import { POLICIES, type TokenPack } from '../PackageTokenSettings';
import { BASEMAP_OPTIONS, type PackageFormData } from './packageForm';
import { HIDDEN_SETTINGS, PLAN_SECTIONS, fieldsOf, type PlanField } from './planSections';

export interface FieldChange { key: string; label: string; from: string; to: string }

/** Names for settings kept on a plan but hidden from the editor, so old history still reads. */
const HIDDEN_LABELS: Record<string, string> = {
  weather_gfs: 'GFS Weather',
  weather_ecmwf: 'ECMWF Weather',
  weather_wavewatch: 'WaveWatch III',
  weather_briefing: 'Weather Briefing',
  forecast_duration_hours: 'Old forecast length',
  vortex_status_panel: 'Status Panel',
  tide_watch: 'TideWatch',
  vt_tide_alerts: 'Tide Alerts',
  vortex_tide_prediction_days: 'Tide prediction horizon',
  priority_generation: 'Priority Generation',
  api_access: 'API Access',
};

/** The token block is one field in the editor but three settings. */
const TOKEN_LABELS: Record<string, string> = {
  token_packs: 'Top-up packs',
  out_of_tokens_policy: 'When this plan runs out',
  out_of_tokens_amount: 'Run-out limit',
};

const fields = PLAN_SECTIONS.flatMap(fieldsOf);
const fieldFor = (key: string): PlanField | undefined => fields.find((f) => f.keys.includes(key as keyof PackageFormData));

/** Every setting in the order the editor shows them, then the hidden ones. */
const ORDER = [...fields.flatMap((f) => f.keys as string[]), ...Object.keys(HIDDEN_SETTINGS)];

const MAX_TEXT = 80;

export function showValue(key: string, value: unknown): string {
  if (key === 'out_of_tokens_policy') return POLICIES.find((p) => p.value === (value ?? ''))?.label ?? String(value);
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (key === 'allowed_basemaps' || key === 'allowed_basemaps_offline') {
    const list = value as string[];
    return list.length ? list.map((v) => BASEMAP_OPTIONS.find((b) => b.value === v)?.label ?? v).join(', ') : 'None';
  }
  if (key === 'token_packs') {
    const packs = value as TokenPack[];
    return packs.length
      ? packs.map((p) => `${p.tokens.toLocaleString('en-NZ')} tokens${p.on_sale ? '' : ' (not on sale)'}`).join(', ')
      : 'None';
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';

  const field = fieldFor(key);
  if (typeof value === 'number' && field) {
    switch (field.kind) {
      case 'money': return `NZ$${(value / 100).toFixed(2)}`;
      case 'storage': return value % 1024 === 0 ? `${value / 1024} GB` : `${value} MB`;
      case 'count': return value === 0 ? 'Unlimited' : String(value);
      case 'slider': return field.describe(value);
      case 'select': return field.options.find((o) => o.value === value)?.label ?? String(value);
      case 'number': return `${value.toLocaleString('en-NZ')}${field.suffix ? ` ${field.suffix}` : ''}`;
    }
  }
  if (field?.kind === 'select') return field.options.find((o) => o.value === value)?.label ?? String(value);
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT - 1)}…` : text;
}

export function describeChange(key: string, from: unknown, to: unknown): FieldChange {
  const label = HIDDEN_LABELS[key] ? `${HIDDEN_LABELS[key]} (hidden)`
    : TOKEN_LABELS[key] ?? fieldFor(key)?.label ?? key;
  return { key, label, from: showValue(key, from), to: showValue(key, to) };
}

/** The settings that differ between two versions of a plan, in the editor's order. */
export function changedFields(before: PackageFormData, after: PackageFormData): FieldChange[] {
  const b = before as unknown as Record<string, unknown>;
  const a = after as unknown as Record<string, unknown>;
  return ORDER.filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k])).map((k) => describeChange(k, b[k], a[k]));
}
